import jsYaml from 'js-yaml'

export interface StoryImportResult {
  id: string
  title: string
  version: string
  description: string
  characterName: string
  characterVersion: number
  sourceFilename: string
  inDatabase: boolean
  onDisk: boolean
}

export interface StoryValidationResult {
  valid: boolean
  errors: string[]
  summary?: {
    id: string
    title?: string
    character: string
    startPhase: string
    phaseCount: number
  }
}

export function validateStoryYaml(yaml: string): StoryValidationResult {
  const errors: string[] = []
  let doc: unknown
  try {
    doc = jsYaml.load(yaml)
  } catch (e) {
    return { valid: false, errors: [`YAML parse error: ${(e as Error).message}`] }
  }

  const d = doc as Record<string, unknown>
  const story = d?.story as Record<string, unknown> | undefined
  if (!story) errors.push('Missing required top-level "story" key')

  const id = story?.id as string | undefined
  if (!id) errors.push('story.id is required')

  const startPhase = story?.start_phase as string | undefined
  if (!startPhase) errors.push('story.start_phase is required')

  const characterName = (story?.character as Record<string, unknown> | undefined)?.name as string | undefined
  if (!characterName) errors.push('story.character.name is required')

  const phases = d?.phases as Record<string, unknown> | undefined
  if (!phases) {
    errors.push('phases map is required')
  } else {
    const phaseKeys = Object.keys(phases)
    if (phaseKeys.length === 0) errors.push('At least one phase is required')
    if (startPhase && !phaseKeys.includes(startPhase))
      errors.push(`story.start_phase "${startPhase}" does not reference a valid phase key`)

    for (const [phaseId, phase] of Object.entries(phases)) {
      const p = phase as Record<string, unknown>
      const objectives = p?.objectives as unknown[] | undefined
      if (!objectives?.length) {
        errors.push(`Phase "${phaseId}": at least one objective is required`)
      } else {
        objectives.forEach((obj, i) => {
          const o = obj as Record<string, unknown>
          if (!o?.id) errors.push(`Phase "${phaseId}" objective[${i}]: id is required`)
          if (!(o?.completion as Record<string, unknown> | undefined)?.type)
            errors.push(`Phase "${phaseId}" objective[${i}]: completion.type is required`)
        })
      }
      ;(p?.prefabs as unknown[] | undefined)?.forEach((t, ti) => {
        const tr = t as Record<string, unknown>
        if (tr?.goto && !phaseKeys.includes(tr.goto as string))
          errors.push(`Phase "${phaseId}" prefab[${ti}]: goto "${tr.goto}" is not a valid phase key`)
      })
    }
  }

  if (errors.length > 0) return { valid: false, errors }
  return {
    valid: true,
    errors: [],
    summary: {
      id: id!,
      title: story?.title as string | undefined,
      character: characterName!,
      startPhase: startPhase!,
      phaseCount: Object.keys(phases ?? {}).length,
    },
  }
}

export interface ServerValidationIssue {
  path: string
  message: string
  line?: number
}

export interface ServerValidationSummary {
  specVersion?: string
  id?: string
  title?: string
  version?: string
  startPhase?: string
  character?: string
  phaseCount: number
  cannedMomentCount: number
}

export interface ServerValidationResult {
  valid: boolean
  errors: ServerValidationIssue[]
  summary?: ServerValidationSummary
}

export async function validateStoryOnServer(yaml: string, apiKey: string): Promise<ServerValidationResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/yaml' }
  if (apiKey) headers['X-API-Key'] = apiKey
  const res = await fetch('/api/stories/validate', { method: 'POST', headers, body: yaml })
  const text = await res.text()
  if (!res.ok) {
    // Auth/network/method errors surface as exceptions.
    let msg = `${res.status} ${res.statusText}`
    try {
      const parsed = JSON.parse(text)
      if (parsed?.error) msg = parsed.error
    } catch {
      if (text) msg = text
    }
    throw new Error(msg)
  }
  return JSON.parse(text) as ServerValidationResult
}

export async function importStory(yaml: string, apiKey: string): Promise<StoryImportResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/yaml' }
  if (apiKey) headers['X-API-Key'] = apiKey
  const res = await fetch('/api/stories/import', { method: 'POST', headers, body: yaml })
  const text = await res.text()
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`
    try {
      const parsed = JSON.parse(text)
      if (parsed?.error) msg = parsed.error
    } catch {
      if (text) msg = text
    }
    throw new Error(msg)
  }
  return JSON.parse(text) as StoryImportResult
}
